import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, db, googleProvider } from '../config/firebase';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import type { UserProfile } from '../types/quotes';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper function to setup Google user profile
  const handleGoogleUserSetup = async (user: User) => {
    try {
      console.log('🔄 Setting up user profile for:', user.email);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        const isEligible = user.email?.endsWith('@chimperator-live.de') || 
                          user.email?.endsWith('@imwizemann.de') || false;
        const isTeamMember = user.email?.endsWith('@chimperator-live.de') || 
                            user.email?.endsWith('@imwizemann.de') || false;
        
        console.log('📝 Creating new customer profile...');
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          displayName: user.displayName || user.email?.split('@')[0],
          role: 'customer',
          isEligibleForDiscount: isEligible,
          isTeamMember: isTeamMember,
          createdAt: Timestamp.now(),
          lastLoginAt: Timestamp.now()
        });
        
        console.log('✅ New customer profile created:', user.email, 'Discount eligible:', isEligible, 'Team member:', isTeamMember);
      } else {
        console.log('🔄 Updating existing user...');
        await setDoc(doc(db, 'users', user.uid), {
          lastLoginAt: Timestamp.now()
        }, { merge: true });
        console.log('✅ User last login updated');
      }
    } catch (error: any) {
      console.error('❌ Error in handleGoogleUserSetup:', error);
      console.error('Error code:', error?.code);
      console.error('Error message:', error?.message);
      throw error; // Re-throw to be caught by caller
    }
  };

  useEffect(() => {
    // Check for redirect result (Google Sign-In)
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          console.log('✅ Google Sign-In successful via redirect');
          console.log('User email:', result.user.email);
          await handleGoogleUserSetup(result.user);
          console.log('✅ User setup completed successfully');
        }
      } catch (error: any) {
        console.error('❌ Redirect result error:', error);
        console.error('Error code:', error?.code);
        console.error('Error message:', error?.message);
        alert(`Anmeldefehler: ${error?.message || 'Unbekannter Fehler'}`);
      }
    };

    checkRedirectResult();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      
      if (firebaseUser) {
        try {
          console.log('🔍 Fetching user profile for:', firebaseUser.uid);
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            console.log('✅ User profile found:', data.email, 'Role:', data.role);
            setUserProfile({
              id: firebaseUser.uid,
              email: data.email,
              displayName: data.displayName,
              role: data.role,
              isEligibleForDiscount: data.isEligibleForDiscount || false,
              isTeamMember: data.isTeamMember || false,
              companyName: data.companyName,
              createdAt: data.createdAt?.toDate() || new Date(),
              lastLoginAt: data.lastLoginAt?.toDate()
            } as UserProfile);
          } else {
            console.warn('⚠️ User document not found yet, waiting for creation...');
            // Wait a moment and try again (for new user creation)
            setTimeout(async () => {
              const retryDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
              if (retryDoc.exists()) {
                const data = retryDoc.data();
                console.log('✅ User profile found on retry:', data.email);
                setUserProfile({
                  id: firebaseUser.uid,
                  email: data.email,
                  displayName: data.displayName,
                  role: data.role,
                  isEligibleForDiscount: data.isEligibleForDiscount || false,
                  isTeamMember: data.isTeamMember || false,
                  companyName: data.companyName,
                  createdAt: data.createdAt?.toDate() || new Date(),
                  lastLoginAt: data.lastLoginAt?.toDate()
                } as UserProfile);
              }
            }, 1000);
          }
        } catch (error) {
          console.error('❌ Error fetching user profile:', error);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signInWithGoogle = async () => {
    try {
      // Try popup first (works better in development)
      const result = await signInWithPopup(auth, googleProvider);
      await handleGoogleUserSetup(result.user);
    } catch (error: any) {
      // If popup fails (e.g., blocked or invalid continue URI), use redirect
      if (error.code === 'auth/popup-blocked' || 
          error.code === 'auth/popup-closed-by-user' ||
          error.code === 'auth/invalid-continue-uri' ||
          error.code === 'auth/unauthorized-domain') {
        console.log('🔄 Popup failed, using redirect instead...');
        await signInWithRedirect(auth, googleProvider);
      } else {
        throw error;
      }
    }
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, userProfile, loading, signIn, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

